import React, { useState, useEffect, useContext, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import FormHelperText from "@material-ui/core/FormHelperText";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";

import SpeedIcon from "@material-ui/icons/Speed";
import AssignmentIcon from "@material-ui/icons/Assignment";
import GroupIcon from "@material-ui/icons/Group";
import TimerIcon from "@material-ui/icons/Timer";
import DoneIcon from "@material-ui/icons/Done";
import HeadsetMicIcon from "@material-ui/icons/HeadsetMic";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import CardCounter from "../../components/Dashboard/CardCounter";
import ButtonWithSpinner from "../../components/ButtonWithSpinner";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { isEmpty } from "lodash";
import moment from "moment";
import { useDate } from "../../hooks/useDate";
import TicketReportsCharts from "./TicketReportsCharts";
import TicketReportsExtended from "./TicketReportsExtended";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  tableWrapper: {
    overflowX: "auto",
  },
  alignRight: {
    textAlign: "right",
  },
  fullWidth: {
    width: "100%",
  },
}));

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const TicketReports = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { datetimeToClient } = useDate();

  const [filterType, setFilterType] = useState(2);
  const [period, setPeriod] = useState(30);
  const [dateFrom, setDateFrom] = useState(
    moment().startOf("month").format("YYYY-MM-DD")
  );
  const [dateTo, setDateTo] = useState(moment().format("YYYY-MM-DD"));

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [summary, setSummary] = useState({});
  const [charts, setCharts] = useState(null);
  const [extended, setExtended] = useState(null);
  const [rows, setRows] = useState([]);
  const [queues, setQueues] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [whatsApps, setWhatsApps] = useState([]);
  const [tagsList, setTagsList] = useState([]);
  const [filterQueueId, setFilterQueueId] = useState("");
  const [filterUserId, setFilterUserId] = useState("");
  const [filterWhatsappId, setFilterWhatsappId] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterTagId, setFilterTagId] = useState("");
  const [filterSlaWait, setFilterSlaWait] = useState("");
  const [filterSlaSupport, setFilterSlaSupport] = useState("");
  const [filterCost, setFilterCost] = useState("");
  const [filterGoal, setFilterGoal] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const buildQueryParams = useCallback(
    (pageNum) => {
      const params = { page: pageNum, pageSize };
      if (filterType === 2 && period > 0) {
        params.days = period;
      }
      if (filterType === 1) {
        if (!isEmpty(dateFrom) && moment(dateFrom).isValid()) {
          params.date_from = moment(dateFrom).format("YYYY-MM-DD");
        }
        if (!isEmpty(dateTo) && moment(dateTo).isValid()) {
          params.date_to = moment(dateTo).format("YYYY-MM-DD");
        }
      }
      if (filterQueueId) params.queue_id = filterQueueId;
      if (filterUserId) params.user_id = filterUserId;
      if (filterWhatsappId) params.whatsapp_id = filterWhatsappId;
      if (filterStatus) params.ticket_status = filterStatus;
      if (filterTagId) params.tag_id = filterTagId;
      if (filterSlaWait) params.sla_wait_minutes = filterSlaWait;
      if (filterSlaSupport) params.sla_support_minutes = filterSlaSupport;
      if (filterCost) params.cost_per_minute = filterCost;
      if (filterGoal) params.goal_wait_minutes = filterGoal;
      return params;
    },
    [
      filterType,
      period,
      dateFrom,
      dateTo,
      filterQueueId,
      filterUserId,
      filterWhatsappId,
      filterStatus,
      filterTagId,
      filterSlaWait,
      filterSlaSupport,
      filterCost,
      filterGoal,
    ]
  );

  const fetchData = useCallback(
    async (pageNum = 1) => {
      if (filterType === 1) {
        if (isEmpty(dateFrom) || isEmpty(dateTo)) {
          return;
        }
      }
      if (filterType === 2 && period === 0) {
        return;
      }
      setLoading(true);
      try {
        const p = buildQueryParams(pageNum);
        const { data } = await api.get("/reports/tickets", { params: p });
        setSummary(data.summary || {});
        setCharts(data.charts || null);
        setExtended(data.extended || null);
        setRows(data.rows || []);
        setTotalRows(data.totalRows || 0);
        setPage(data.page || pageNum);
      } catch (err) {
        toastError(err);
      }
      setLoading(false);
    },
    [buildQueryParams, filterType, dateFrom, dateTo, period]
  );

  useEffect(() => {
    if (user?.profile === "admin") {
      fetchData(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.profile]);

  useEffect(() => {
    if (user?.profile !== "admin") return;
    const load = async () => {
      try {
        const [q, u, w, t] = await Promise.all([
          api.get("/queue"),
          api.get("/users/list"),
          api.get("/whatsapp"),
          api.get("/tags/list"),
        ]);
        setQueues(q.data || []);
        setUsersList(u.data || []);
        setWhatsApps(w.data || []);
        setTagsList(t.data || []);
      } catch (e) {
        toastError(e);
      }
    };
    load();
  }, [user?.profile]);

  const handleFilterClick = () => {
    if (filterType === 1) {
      if (isEmpty(dateFrom) || isEmpty(dateTo)) {
        return;
      }
    }
    if (filterType === 2 && period === 0) {
      return;
    }
    fetchData(1);
  };

  const handleExport = async (format) => {
    if (filterType === 1 && (isEmpty(dateFrom) || isEmpty(dateTo))) {
      return;
    }
    if (filterType === 2 && period === 0) {
      return;
    }
    setExporting(true);
    try {
      const params = { ...buildQueryParams(1), format };
      delete params.page;
      delete params.pageSize;
      const response = await api.get("/reports/tickets/export", {
        params,
        responseType: "blob",
      });
      const ext = format === "xlsx" ? "xlsx" : "csv";
      const stamp = moment().format("YYYY-MM-DD_HH-mm");
      downloadBlob(
        response.data,
        `relatorio-atendimentos-${stamp}.${ext}`
      );
    } catch (err) {
      toastError(err);
    }
    setExporting(false);
  };

  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));

  if (user?.profile !== "admin") {
    return (
      <MainContainer>
        <MainHeader>
          <Title>{i18n.t("ticketReports.title")}</Title>
        </MainHeader>
        <Paper className={classes.mainPaper} variant="outlined">
          <Typography>{i18n.t("ticketReports.forbidden")}</Typography>
        </Paper>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("ticketReports.title")}</Title>
        <MainHeaderButtonsWrapper>
          <ButtonWithSpinner
            loading={exporting}
            color="primary"
            variant="outlined"
            onClick={() => handleExport("csv")}
          >
            {i18n.t("ticketReports.exportCsv")}
          </ButtonWithSpinner>
          <ButtonWithSpinner
            loading={exporting}
            color="primary"
            variant="contained"
            onClick={() => handleExport("xlsx")}
          >
            {i18n.t("ticketReports.exportXlsx")}
          </ButtonWithSpinner>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl className={classes.fullWidth}>
              <InputLabel id="tr-filter-type">
                {i18n.t("ticketReports.filterType")}
              </InputLabel>
              <Select
                labelId="tr-filter-type"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value={1}>
                  {i18n.t("ticketReports.filterByDate")}
                </MenuItem>
                <MenuItem value={2}>
                  {i18n.t("ticketReports.filterByPeriod")}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {filterType === 1 ? (
            <>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label={i18n.t("ticketReports.dateFrom")}
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className={classes.fullWidth}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label={i18n.t("ticketReports.dateTo")}
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className={classes.fullWidth}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          ) : (
            <Grid item xs={12} sm={6} md={4}>
              <FormControl className={classes.fullWidth}>
                <InputLabel id="tr-period">{i18n.t("ticketReports.period")}</InputLabel>
                <Select
                  labelId="tr-period"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                >
                  <MenuItem value={0}>—</MenuItem>
                  <MenuItem value={3}>3</MenuItem>
                  <MenuItem value={7}>7</MenuItem>
                  <MenuItem value={15}>15</MenuItem>
                  <MenuItem value={30}>30</MenuItem>
                  <MenuItem value={60}>60</MenuItem>
                  <MenuItem value={90}>90</MenuItem>
                </Select>
                <FormHelperText>{i18n.t("ticketReports.period")}</FormHelperText>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} className={classes.alignRight}>
            <ButtonWithSpinner
              loading={loading}
              color="primary"
              variant="contained"
              onClick={handleFilterClick}
            >
              {i18n.t("ticketReports.filter")}
            </ButtonWithSpinner>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl className={classes.fullWidth}>
              <InputLabel>{i18n.t("ticketReports.filterQueue")}</InputLabel>
              <Select
                value={filterQueueId}
                onChange={(e) => setFilterQueueId(e.target.value)}
              >
                <MenuItem value="">—</MenuItem>
                {queues.map((q) => (
                  <MenuItem key={q.id} value={q.id}>
                    {q.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl className={classes.fullWidth}>
              <InputLabel>{i18n.t("ticketReports.filterUser")}</InputLabel>
              <Select
                value={filterUserId}
                onChange={(e) => setFilterUserId(e.target.value)}
              >
                <MenuItem value="">—</MenuItem>
                {usersList.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl className={classes.fullWidth}>
              <InputLabel>{i18n.t("ticketReports.filterWhatsapp")}</InputLabel>
              <Select
                value={filterWhatsappId}
                onChange={(e) => setFilterWhatsappId(e.target.value)}
              >
                <MenuItem value="">—</MenuItem>
                {whatsApps.map((w) => (
                  <MenuItem key={w.id} value={w.id}>
                    {w.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl className={classes.fullWidth}>
              <InputLabel>{i18n.t("ticketReports.filterStatus")}</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="">—</MenuItem>
                <MenuItem value="pending">pending</MenuItem>
                <MenuItem value="open">open</MenuItem>
                <MenuItem value="closed">closed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl className={classes.fullWidth}>
              <InputLabel>{i18n.t("ticketReports.filterTag")}</InputLabel>
              <Select
                value={filterTagId}
                onChange={(e) => setFilterTagId(e.target.value)}
              >
                <MenuItem value="">—</MenuItem>
                {tagsList.map((tg) => (
                  <MenuItem key={tg.id} value={tg.id}>
                    {tg.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              className={classes.fullWidth}
              label={i18n.t("ticketReports.filterSlaWait")}
              type="number"
              value={filterSlaWait}
              onChange={(e) => setFilterSlaWait(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              className={classes.fullWidth}
              label={i18n.t("ticketReports.filterSlaSupport")}
              type="number"
              value={filterSlaSupport}
              onChange={(e) => setFilterSlaSupport(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              className={classes.fullWidth}
              label={i18n.t("ticketReports.filterCost")}
              type="number"
              value={filterCost}
              onChange={(e) => setFilterCost(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              className={classes.fullWidth}
              label={i18n.t("ticketReports.filterGoal")}
              type="number"
              value={filterGoal}
              onChange={(e) => setFilterGoal(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} className={classes.alignRight}>
            <Button
              size="small"
              onClick={() => {
                setFilterQueueId("");
                setFilterUserId("");
                setFilterWhatsappId("");
                setFilterStatus("");
                setFilterTagId("");
                setFilterSlaWait("");
                setFilterSlaSupport("");
                setFilterCost("");
                setFilterGoal("");
              }}
            >
              {i18n.t("ticketReports.filterClear")}
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6">{i18n.t("ticketReports.summary")}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <CardCounter
              icon={<AssignmentIcon fontSize="inherit" />}
              title={i18n.t("ticketReports.totalTrackings")}
              value={summary.totalTrackings}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <CardCounter
              icon={<DoneIcon fontSize="inherit" />}
              title={i18n.t("ticketReports.finalized")}
              value={summary.finalizedTrackings}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <CardCounter
              icon={<HeadsetMicIcon fontSize="inherit" />}
              title={i18n.t("ticketReports.openTrackings")}
              value={summary.openTrackings}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <CardCounter
              icon={<GroupIcon fontSize="inherit" />}
              title={i18n.t("ticketReports.uniqueTickets")}
              value={summary.uniqueTickets}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <CardCounter
              icon={<SpeedIcon fontSize="inherit" />}
              title={i18n.t("ticketReports.avgSupport")}
              value={
                summary.avgSupportMinutes != null
                  ? Number(summary.avgSupportMinutes).toFixed(1)
                  : "—"
              }
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <CardCounter
              icon={<TimerIcon fontSize="inherit" />}
              title={i18n.t("ticketReports.avgWait")}
              value={
                summary.avgWaitMinutes != null
                  ? Number(summary.avgWaitMinutes).toFixed(1)
                  : "—"
              }
              loading={loading}
            />
          </Grid>

          <TicketReportsExtended extended={extended} loading={loading} />

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              {i18n.t("ticketReports.chartsSection")}
            </Typography>
            <TicketReportsCharts charts={charts} loading={loading} />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6">{i18n.t("ticketReports.tableTitle")}</Typography>
            <div className={classes.tableWrapper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{i18n.t("ticketReports.status")}</TableCell>
                    <TableCell>{i18n.t("ticketReports.contact")}</TableCell>
                    <TableCell>{i18n.t("ticketReports.phone")}</TableCell>
                    <TableCell>{i18n.t("ticketReports.attendant")}</TableCell>
                    <TableCell>{i18n.t("ticketReports.queue")}</TableCell>
                    <TableCell>{i18n.t("ticketReports.connection")}</TableCell>
                    <TableCell>{i18n.t("ticketReports.queuedAt")}</TableCell>
                    <TableCell>{i18n.t("ticketReports.startedAt")}</TableCell>
                    <TableCell>{i18n.t("ticketReports.finishedAt")}</TableCell>
                    <TableCell>{i18n.t("ticketReports.tags")}</TableCell>
                    <TableCell align="right">
                      {i18n.t("ticketReports.supportMin")}
                    </TableCell>
                    <TableCell align="right">
                      {i18n.t("ticketReports.waitMin")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.trackingId}>
                      <TableCell>{r.ticketStatus}</TableCell>
                      <TableCell>{r.contactName}</TableCell>
                      <TableCell>{r.contactNumber}</TableCell>
                      <TableCell>{r.attendantName}</TableCell>
                      <TableCell>{r.queueName}</TableCell>
                      <TableCell>{r.connectionName}</TableCell>
                      <TableCell>
                        {r.queuedAt ? datetimeToClient(r.queuedAt) : ""}
                      </TableCell>
                      <TableCell>
                        {r.startedAt ? datetimeToClient(r.startedAt) : ""}
                      </TableCell>
                      <TableCell>
                        {r.finishedAt ? datetimeToClient(r.finishedAt) : ""}
                      </TableCell>
                      <TableCell>{r.tags || ""}</TableCell>
                      <TableCell align="right">
                        {r.supportMinutes != null ? r.supportMinutes : ""}
                      </TableCell>
                      <TableCell align="right">
                        {r.waitMinutes != null ? r.waitMinutes : ""}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <Grid
              container
              spacing={2}
              alignItems="center"
              justifyContent="flex-end"
              style={{ marginTop: 8 }}
            >
              <Grid item>
                <Typography variant="body2">
                  {i18n.t("ticketReports.page")} {page} {i18n.t("ticketReports.of")}{" "}
                  {totalPages} ({totalRows})
                </Typography>
              </Grid>
              <Grid item>
                <Button
                  size="small"
                  disabled={page <= 1 || loading}
                  onClick={() => fetchData(page - 1)}
                >
                  {i18n.t("ticketReports.previous")}
                </Button>
                <Button
                  size="small"
                  disabled={page >= totalPages || loading}
                  onClick={() => fetchData(page + 1)}
                >
                  {i18n.t("ticketReports.next")}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </MainContainer>
  );
};

export default TicketReports;
